import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const inputVariants = cva(
  "flex w-full rounded-apx-lg border bg-apx-bg-card text-apx-text-primary ring-offset-background transition-all duration-apx-normal ease-apx-ease file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-apx-text-tertiary disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-apx-bg-primary",
  {
    variants: {
      variant: {
        default:
          "border-apx-border focus:outline-none focus:border-insight-500 focus:ring-2 focus:ring-insight-500/30",
        error:
          "border-destructive focus:outline-none focus:border-destructive focus:ring-2 focus:ring-destructive/30",
        success:
          "border-tier-exact focus:outline-none focus:border-tier-exact focus:ring-2 focus:ring-tier-exact/30",
        ghost:
          "border-transparent bg-transparent focus:outline-none focus:bg-apx-bg-hover focus:border-apx-border",
      },
      inputSize: {
        default: "h-10 px-3 py-2 text-sm",
        sm: "h-8 px-2 py-1 text-xs rounded-apx-md",
        lg: "h-12 px-4 py-3 text-base rounded-apx-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  }
)

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size">,
    VariantProps<typeof inputVariants> {
  error?: boolean
  success?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      variant,
      inputSize,
      error,
      success,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    // Determine variant based on error/success props if not explicitly set
    const computedVariant = error ? "error" : success ? "success" : variant

    if (leftIcon || rightIcon) {
      return (
        <div className="relative flex items-center">
          {leftIcon && (
            <div className="absolute left-3 flex items-center pointer-events-none text-apx-text-tertiary">
              {leftIcon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              inputVariants({ variant: computedVariant, inputSize }),
              leftIcon && "pl-10",
              rightIcon && "pr-10",
              className
            )}
            ref={ref}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 flex items-center pointer-events-none text-apx-text-tertiary">
              {rightIcon}
            </div>
          )}
        </div>
      )
    }

    return (
      <input
        type={type}
        className={cn(
          inputVariants({ variant: computedVariant, inputSize }),
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

// Textarea with APX styling
const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea"> & {
    error?: boolean
    success?: boolean
  }
>(({ className, error, success, ...props }, ref) => {
  const variant = error ? "error" : success ? "success" : "default"

  return (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-apx-lg border bg-apx-bg-card px-3 py-2 text-sm text-apx-text-primary ring-offset-background transition-all duration-apx-normal ease-apx-ease placeholder:text-apx-text-tertiary disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-apx-bg-primary resize-none",
        variant === "default" &&
          "border-apx-border focus:outline-none focus:border-insight-500 focus:ring-2 focus:ring-insight-500/30",
        variant === "error" &&
          "border-destructive focus:outline-none focus:border-destructive focus:ring-2 focus:ring-destructive/30",
        variant === "success" &&
          "border-tier-exact focus:outline-none focus:border-tier-exact focus:ring-2 focus:ring-tier-exact/30",
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = "Textarea"

export { Input, Textarea, inputVariants }
